import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appShortcuts } from 'src/constants/appShortcuts';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useRegisterShortcuts } from 'src/utils/hooks';
import { QuickAction, QuickActionKey, QuickActionsContext } from './quick-actions-context';

type QuickActionsProviderProps = {
  children: React.ReactNode;
};

export const QuickActionsProvider: React.FC<QuickActionsProviderProps> = ({ children }) => {
  const [actions, setActions] = useState<Map<QuickActionKey, QuickAction>>(new Map());

  const { showModal, setModalProps } = useModalMethods(Modals.QuickActionsModal);
  const { modalProps } = useModalState(Modals.QuickActionsModal);

  const { isDisabled } = modalProps || {};

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.quickActions,
        callback: () => {
          if (isDisabled) return;
          showModal();
          return false;
        },
      }),
      [isDisabled, showModal],
    ),
  );

  const addActions = useCallback((newActions: QuickAction | QuickAction[]) => {
    setActions((prevActions) => {
      const updatedActions = new Map(prevActions);

      (Array.isArray(newActions) ? newActions : [newActions]).forEach((action) => {
        if (action.handler) {
          updatedActions.set(action.id || action.handler, action);
        }
      });
      return updatedActions;
    });
  }, []);

  const removeActions = useCallback((newActions: QuickAction | QuickAction[]) => {
    setActions((prevActions) => {
      const updatedActions = new Map(prevActions);

      (Array.isArray(newActions) ? newActions : [newActions]).forEach((action) => {
        if (action.handler) {
          updatedActions.delete(action.id || action.handler);
        }
      });

      return updatedActions;
    });
  }, []);

  // This function updates the action and returns a function to restore its original state.
  // It is usefull when in some views the look and behaviour is slightly different
  // than it was in the original one
  const updateAndRestoreAction = useCallback(
    (actionKey: QuickActionKey, data: Partial<QuickAction>, options?: { skip?: boolean }) => {
      if (options?.skip) return () => undefined;
      let originAction: QuickAction | null = null;
      setActions((prevActions) => {
        const updatedActions = new Map(prevActions);
        originAction = prevActions.get(actionKey) ?? null;
        if (originAction) {
          updatedActions.set(actionKey, { ...originAction, ...data });
        }
        return updatedActions;
      });

      return () => {
        setActions((prevActions) => {
          const updatedActions = new Map(prevActions);
          if (originAction) {
            updatedActions.set(actionKey, originAction);
          }
          return updatedActions;
        });
      };
    },
    [],
  );

  const getActionById = useCallback(
    (actionKey: QuickActionKey) => {
      return actions.get(actionKey) || null;
    },
    [actions],
  );

  const value = useMemo(
    () => ({ addActions, updateAndRestoreAction, removeActions, getActionById }),
    [addActions, removeActions, updateAndRestoreAction, getActionById],
  );

  const actionsArray = useMemo(() => {
    return Array.from(actions.values());
  }, [actions]);

  useEffect(() => {
    setModalProps(
      {
        actions: actionsArray,
      },
      'staticKey',
    );
  }, [actionsArray, setModalProps]);

  return <QuickActionsContext.Provider value={value}>{children}</QuickActionsContext.Provider>;
};
